import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import type { AiAutocompletePluginState } from '../types/types';

const GHOST_INLINE_CLASS = 'heading-autocomplete-ghost-inline';
const GHOST_HINT_CLASS = 'heading-autocomplete-ghost-text-hint';

const ghostInlineStyle = convertToInlineCss({
	color: token('color.text.subtlest'),
	opacity: 0.6,
	pointerEvents: 'none',
	userSelect: 'none',
	fontStyle: 'italic',
	whiteSpace: 'pre-wrap',
});

const ghostHintStyle = convertToInlineCss({
	display: 'block',
	color: token('color.text.disabled'),
	font: token('font.body.small'),
	marginTop: token('space.050'),
	pointerEvents: 'none',
	userSelect: 'none',
	fontStyle: 'italic',
});

/**
 * Builds the appropriate ProseMirror DecorationSet for the current plugin state.
 *
 * • heading-idle trigger → widget decoration after the heading node (ghost paragraph)
 * • manual trigger       → inline widget decoration at the cursor (ghost inline text)
 *
 * All decorations are aria-hidden and non-editable — they never touch the doc.
 */
export function buildSuggestionDecoration(
	state: EditorState,
	pluginState: Extract<AiAutocompletePluginState, { status: 'suggestion' }>,
): DecorationSet {
	return buildInlineGhostDecoration(state, pluginState.cursorPos, pluginState.suggestion);
}

/**
 * Inline ghost text at the cursor position (manual Cmd+Shift+Space trigger).
 * Accepted with Tab.
 */
function buildInlineGhostDecoration(
	state: EditorState,
	cursorPos: number,
	suggestion: string,
): DecorationSet {
	const widget = Decoration.widget(
		cursorPos,
		() => {
			const span = document.createElement('span');
			span.className = GHOST_INLINE_CLASS;
			span.setAttribute('aria-hidden', 'true');
			span.setAttribute('contenteditable', 'false');
			span.setAttribute('data-testid', 'heading-autocomplete-ghost-inline');
			span.style.cssText = ghostInlineStyle;

			const text = document.createTextNode(suggestion);
			span.appendChild(text);

			const hint = document.createElement('span');
			hint.className = GHOST_HINT_CLASS;
			hint.style.cssText = ghostHintStyle;
			hint.textContent = ' Tab Accept · Esc Dismiss';
			span.appendChild(hint);

			return span;
		},
		{ side: 1, key: 'heading-autocomplete-ghost-inline' },
	);

	return DecorationSet.create(state.doc, [widget]);
}
